/**
 * Module Types - Centralized type definitions
 *
 * This file contains all types and interfaces used across different modules
 * in the application for better type management and consistency.
 */

// ==========================================
// PROMOTIONS & DISCOUNTS MODULE TYPES
// ==========================================

/**
 * Form data structure for creating and updating promotions/discounts
 */
export interface PromotionFormData {
  name: string;
  discountCode: string;
  discountType: 'percentage' | 'fixed-amount';
  discountValue: number;
  startDate: string;
  endDate: string;
  description?: string;
  eligibleItems: 'all' | 'specific';
  eligibleCustomers: 'all' | 'b2b' | 'b2c';
  eligibleItemIds?: string[];
  minimumPurchaseRequirement?: number;
  usageLimit?: number;
  allowStacking?: boolean;
  status?: 'active' | 'inactive' | 'expired' | 'pending';
  termsAndConditions?: string;
}

/**
 * Discount data structure for API responses and component props
 */
export interface DiscountData {
  _id: string;
  name?: string;
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
  status: string;
  validFrom: string;
  validTo: string;
  description?: string;
  eligibleItems?: 'all' | 'specific';
  eligibleCustomers?: 'all' | 'b2b' | 'b2c';
  minimumPurchaseRequirement?: number;
  usageLimit?: number;
  allowStacking?: boolean;
  termsAndConditions?: string;
  createdAt?: string;
}

/**
 * Props for PromotionForm component
 */
export interface PromotionFormProps {
  mode: 'create' | 'update';
  discount?: DiscountData;
  onSuccess: () => Promise<void>;
  onCancel: () => void;
}

/**
 * Props for discount create sheet component
 */
export interface DiscountCreateSheetProps {
  onDiscountCreated?: () => Promise<void> | void;
}

/**
 * Props for discount update sheet component
 */
export interface DiscountUpdateSheetProps {
  discount: DiscountData;
  onDiscountUpdated?: () => Promise<void> | void;
}

/**
 * Step indicator component props
 */
export interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  stepLabels: string[];
}

// ==========================================
// SHARED FORM OPTION TYPES
// ==========================================

/**
 * Option structure for filter selects and dropdowns
 */
export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Discount type options for forms
 */
export const DISCOUNT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'percentage', label: 'Percentage Discount (%)' },
  { value: 'fixed-amount', label: 'Fixed Amount Discount (₦)' },
];

/**
 * Eligible items options for discount forms
 */
export const ELIGIBLE_ITEMS_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Items' },
  { value: 'specific', label: 'Specific Items' },
];

/**
 * Eligible customers options for discount forms
 */
export const ELIGIBLE_CUSTOMERS_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Customers' },
  { value: 'b2b', label: 'B2B Customers Only' },
  { value: 'b2c', label: 'B2C Customers Only' },
];

/**
 * Status options for discount forms
 */
export const STATUS_OPTIONS: SelectOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'expired', label: 'Expired' },
  { value: 'pending', label: 'Pending' },
];

// ==========================================
// LEGACY PROMOTION TYPES (from types/promotions.ts)
// ==========================================

/**
 * Legacy discount data interface - keeping for backward compatibility
 * @deprecated Use DiscountData instead
 */
export interface LegacyDiscountData {
  _id: string;
  name: string;
  discountCode: string;
  discountType: 'percentage' | 'fixed-amount';
  discountValue: number;
  minimumPurchaseRequirement?: number;
  usageLimit?: number;
  usedCount?: number;
  startDate: string;
  endDate: string;
  status: string;
  description?: string;
  eligibleItems: string;
  eligibleCustomers: string;
  termsAndConditions?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Detailed discount data for show pages
 */
export interface DetailedDiscountData {
  _id: string;
  discountType: string;
  discountValue: string;
  discountCode: string;
  startDate: string;
  endDate: string;
  termsAndConditions: string;
  eligibleItems: string;
  eligibleCustomers: string;
  minimumPurchaseRequirement: number;
  usageLimit: number;
  name: string;
  email: string;
  contactPerson: string;
  phone: number;
  status: string;
  metrics: {
    totalRevenueFromDiscountSales: number;
    totalSalesWithDiscount: number;
  };
}

/**
 * Metrics data interface
 */
export interface PromotionMetrics {
  totalDiscountAvailable: number;
  totalActive: number;
  totalUpcoming: number;
  totalExpired: number;
  totalPending: number;
}

/**
 * Filter state interface for promotions/discounts
 */
export interface PromotionFilterState {
  search: string;
  type: string;
  status: string;
}

/**
 * Pagination state interface for promotions/discounts
 */
export interface PromotionPaginationState {
  page: number;
  limit: number;
}

/**
 * Discount form data for creation - legacy version
 * @deprecated Use PromotionFormData instead
 */
export interface DiscountFormData {
  name: string;
  discountCode: string;
  discountType: 'percentage' | 'fixed-amount';
  discountValue: number;
  minimumPurchaseRequirement?: number;
  usageLimit?: number;
  startDate: string;
  endDate: string;
  description?: string;
  eligibleItems: string;
  eligibleCustomers: string;
  termsAndConditions?: string;
}

/**
 * Discount update data - legacy version
 * @deprecated Use DiscountData instead
 */
export interface DiscountUpdateData {
  _id: string;
  code: string;
  type: 'fixed' | 'percentage';
  value: number;
  status: string;
  validFrom: string;
  validTo: string;
  description?: string;
}

/**
 * Discount export parameters
 */
export interface DiscountExportParams {
  search?: string;
  discountType?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  format?: 'xlsx' | 'csv';
}

/**
 * Discount list response from API
 */
export interface DiscountListResponse {
  discounts: LegacyDiscountData[];
  pagination: {
    page: number;
    limit: number;
    totalDocs: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// ==========================================
// CUSTOMERS MODULE TYPES
// ==========================================

/**
 * Customer form data for creation/updates
 */
export interface CustomerFormData {
  // Customer Details
  accountType: 'b2b' | 'b2c';
  businessName?: string;
  businessType?: string;
  contactName?: string;
  contactNumber: string;
  email: string;
  paymentTerms: 'instant' | 'credit';

  // Delivery Address
  'deliveryAddress.street': string;
  'deliveryAddress.city': string;
  'deliveryAddress.state': string;
  'deliveryAddress.country': string;

  // Business Address (B2B only)
  'businessAddress.street'?: string;
  'businessAddress.city'?: string;
  'businessAddress.state'?: string;
  'businessAddress.country'?: string;

  // KYC (B2B only)
  businessRegistrationNumber?: string;
  businessCertificate?: FileList;
  businessOwnerId?: FileList;

  // Payment Period (Credit only)
  paymentPeriod?: string;
  'creditStatus.type': 'instant' | 'credit';
  creditLimit?: string;
}

/**
 * Props for CustomerForm component
 */
export interface CustomerFormProps {
  customer?: any;
  onSuccess?: (customer: any) => void;
  onCancel?: () => void;
  mode?: 'create' | 'edit';
}

/**
 * Props for customer create sheet component
 */
export interface CustomerCreateSheetProps {
  onCustomerCreated?: () => Promise<void> | void;
}

/**
 * Props for customer update sheet component
 */
export interface CustomerUpdateSheetProps {
  customer: any;
  onCustomerUpdated?: () => Promise<void> | void;
}

// ==========================================
// ORDERS MODULE TYPES
// ==========================================

/**
 * Order form data for creation
 */
export interface OrderFormData {
  orderDate: string;
  customerId: string;
  paymentStatus: 'paid' | 'unpaid';
  deliveryMethod: 'pick-up' | 'dispatch';
  deliveryFee: string;
  deliveryNote: string;
}

/**
 * Order update form data
 */
export interface OrderUpdateFormData {
  status:
    | 'pending'
    | 'confirmed'
    | 'processing'
    | 'packed'
    | 'dispatched'
    | 'delivered'
    | 'cancelled';
  paymentStatus?: 'pending' | 'paid' | 'unpaid';
  notes?: string;
}

/**
 * Customer data for orders
 */
export interface OrderCustomerData {
  _id: string;
  contactName: string;
  businessName?: string;
  email: string;
  accountType: string;
  creditStatus?: {
    type: string;
    limit: number;
    period: number;
    paymentDueDate: string;
  };
}

/**
 * Product data for orders
 */
export interface OrderProductData {
  _id: string;
  name: string;
  price: number;
  measurementUnit: string;
  quantity: number;
  isVariant: boolean;
  variant: Array<{
    _id: string;
    name: string;
    price: number;
    quantity: number;
    reorderLevel: number;
  }>;
}

/**
 * Order item data
 */
export interface OrderItemData {
  id: number;
  name: string;
  quantity: number;
  price: number;
  _id: string;
  variant?: string;
  variantId?: string;
}

/**
 * Order data structure
 */
export interface OrderData {
  _id: string;
  orderId: string;
  orderNumber?: string;
  status: string;
  paymentStatus?: string;
  approvalStatus?: string;
  source?: string;
  notes?: string;
  customer?: {
    contactName: string;
    businessName?: string;
    _id: string;
  };
  Items?: Array<{
    name: string;
    quantity: number;
    unitPrice: number;
    subTotal: number;
  }>;
  totalAmount?: number;
  createdBy?: {
    username: string;
  };
  createdAt?: string;
  deliveryMethod?: string;
  deliveryFee?: number;
}

/**
 * Props for OrderForm component
 */
export interface OrderFormProps {
  mode: 'create' | 'update';
  order?: OrderData;
  onSuccess: () => Promise<void>;
  onCancel: () => void;
}

/**
 * Props for order create sheet component
 */
export interface OrderCreateSheetProps {
  onOrderCreated?: () => Promise<void> | void;
}

/**
 * Props for order update sheet component
 */
export interface OrderUpdateSheetProps {
  order: {
    _id: string;
    orderId: string;
    orderNumber?: string;
    status: string;
    paymentStatus?: string;
    approvalStatus?: string;
    notes?: string;
    customer?: {
      contactName: string;
      businessName?: string;
      _id: string;
    };
    Items?: Array<{
      name: string;
      quantity: number;
      unitPrice: number;
      subTotal: number;
    }>;
    totalAmount?: number;
    createdBy?: {
      username: string;
    };
    createdAt?: string;
    deliveryMethod?: string;
    deliveryFee?: number;
  };
  onOrderUpdated?: () => Promise<void> | void;
}

// ==========================================
// RIDERS MODULE TYPES
// ==========================================

/**
 * Props for RiderForm component
 */
export interface RiderFormProps {
  rider?: any;
  onSuccess?: (rider: any) => void;
  onCancel?: () => void;
  mode?: 'create' | 'edit';
}

/**
 * Props for rider create sheet component
 */
export interface RiderCreateSheetProps {
  onRiderCreated?: () => Promise<void> | void;
}

/**
 * Props for rider update sheet component
 */
export interface RiderUpdateSheetProps {
  rider: any; // Using any for now, could be improved with proper Rider type
  onRiderUpdated?: () => Promise<void> | void;
}

// ==========================================
// SHARED FORM OPTION CONSTANTS
// ==========================================

/**
 * Vehicle type options for rider forms
 */
export const VEHICLE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'bicycle', label: 'Bicycle' },
  { value: 'car', label: 'Car' },
  { value: 'van', label: 'Van' },
  { value: 'truck', label: 'Truck' },
  { value: 'scooter', label: 'Scooter' },
  { value: 'other', label: 'Other' },
];

/**
 * Gender options for forms
 */
export const GENDER_OPTIONS: SelectOption[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

/**
 * Relationship options for emergency contacts
 */
export const RELATIONSHIP_OPTIONS: SelectOption[] = [
  { value: 'parent', label: 'Parent' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'child', label: 'Child' },
  { value: 'friend', label: 'Friend' },
  { value: 'relative', label: 'Relative' },
  { value: 'other', label: 'Other' },
];

/**
 * License type options for riders
 */
export const LICENSE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'driving_license', label: 'Driving License' },
  { value: 'motorcycle_license', label: 'Motorcycle License' },
  { value: 'commercial_license', label: 'Commercial License' },
  { value: 'hazmat', label: 'HAZMAT' },
  { value: 'food_safety', label: 'Food Safety' },
  { value: 'other', label: 'Other' },
];

/**
 * Account type options for customers
 */
export const ACCOUNT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'b2b', label: 'B2B (Business)' },
  { value: 'b2c', label: 'B2C (Individual)' },
];

/**
 * Payment terms options for customers
 */
export const PAYMENT_TERMS_OPTIONS: SelectOption[] = [
  { value: 'instant', label: 'Instant Payment' },
  { value: 'credit', label: 'Credit Payment' },
];

/**
 * Delivery method options for orders
 */
export const DELIVERY_METHOD_OPTIONS: SelectOption[] = [
  { value: 'pick-up', label: 'Pick Up' },
  { value: 'dispatch', label: 'Dispatch' },
];

/**
 * Order status options
 */
export const ORDER_STATUS_OPTIONS: SelectOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'processing', label: 'Processing' },
  { value: 'packed', label: 'Packed' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

/**
 * Payment status options for orders
 */
export const PAYMENT_STATUS_OPTIONS: SelectOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
];

// ==========================================
// SHARED COMPONENT INTERFACES
// ==========================================

/**
 * File upload component props (shared across modules)
 */
export interface FileUploadProps {
  label: string;
  accept: string;
  onChange: (files: FileList | null) => void;
  error?: string;
  supportedFormats?: string;
  value?: FileList | null;
}

/**
 * Date picker component props (shared across modules)
 */
export interface DatePickerProps {
  selectedDate?: Date;
  onDateChange: (date: Date | undefined) => void;
  placeholder?: string;
  error?: string;
  label?: string;
}

// ==========================================
// INVENTORY/TRANSACTIONS MODULE TYPES
// ==========================================

/**
 * Product variant data structure
 */
export interface ProductVariant {
  _id?: string;
  name: string;
  quantity: number;
  price: number;
  reorderLevel: number;
}

/**
 * Product/inventory data structure
 */
export interface ProductData {
  _id: string;
  name: string;
  description?: string;
  category: string;
  measurementUnit: string;
  quantity: number;
  price?: number;
  reorderLevel: number;
  isVariant: boolean;
  variant?: ProductVariant[];
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Form data structure for creating and updating inventory/products
 */
export interface InventoryFormData {
  // Core
  name: string;
  description?: string;
  shortDescription?: string;
  brand?: string;

  // Identification (optional)
  sku?: string;
  barcode?: string;
  internalCode?: string;

  // Category (primary stored in string for form; secondary/subcategory optional)
  category: string;
  categorySecondary?: string;
  categorySubcategory?: string;

  // Classification
  productClass?:
    | 'raw_material'
    | 'finished_good'
    | 'semi_finished'
    | 'consumable'
    | 'service';

  // Measurement Unit (primary stored in string; secondary/conversion optional)
  measurementUnit: string;
  measurementUnitSecondary?: string;
  conversionFactor?: number | null;

  // Simple product stock/pricing
  quantity?: number | null;
  reorderLevel?: number | null;
  costPrice?: number | null;
  sellingPrice?: number | null;
  wholesalePrice?: number | null;
  retailPrice?: number | null;
  currency?: 'NGN' | 'USD' | 'EUR' | 'GBP';

  // Product type
  isVariant: boolean;
  variants?: ProductVariant[]; // variant items use { name, quantity, price, reorderLevel }

  // Advanced
  qualityGrade?: 'Premium' | 'A' | 'B' | 'C' | 'Rejected';

  // Storage & Location
  storageWarehouse?: string;
  storageConditions?:
    | 'ambient'
    | 'refrigerated'
    | 'frozen'
    | 'controlled_atmosphere'
    | 'dry_storage'
    | 'cold_chain_required';
  storageTemperatureMin?: number | null;
  storageTemperatureMax?: number | null;
  storageTemperatureOptimal?: number | null;
  storageHumidityMin?: number | null;
  storageHumidityMax?: number | null;
  storageHumidityOptimal?: number | null;
  storageShelfLifeDays?: number | null;
  storageShelfLifeConditions?: string;
  storageLocationZone?: string;
  storageLocationAisle?: string;
  storageLocationShelf?: string;
  storageLocationBin?: string;

  // Auto reorder
  autoReorderEnabled?: boolean;
  autoReorderPoint?: number | null;
  autoReorderQty?: number | null;
  leadTimeDays?: number | null;

  // Compliance
  requiresCertification?: boolean;
  hazardousFlag?: boolean;
  restrictedSale?: boolean;
  exportEligible?: boolean;
}

/**
 * Props for InventoryForm component
 */
export interface InventoryFormProps {
  mode: 'create' | 'update';
  product?: ProductData;
  onSuccess: () => Promise<void>;
  onCancel: () => void;
}

/**
 * Props for inventory create sheet component
 */
export interface InventoryCreateSheetProps {
  onProductCreated?: () => Promise<void> | void;
}

/**
 * Props for inventory update sheet component
 */
export interface InventoryUpdateSheetProps {
  product: ProductData;
  onProductUpdated?: () => Promise<void> | void;
}

/**
 * Inventory category options
 */
export const INVENTORY_CATEGORY_OPTIONS: SelectOption[] = [
  { value: 'value-pick', label: 'Value Pick' },
  { value: 'fresh-produce', label: 'Fresh Produce' },
  { value: 'packaged-goods', label: 'Packaged Goods' },
  { value: 'dairy', label: 'Dairy' },
  { value: 'meat-poultry', label: 'Meat & Poultry' },
  { value: 'beverages', label: 'Beverages' },
  { value: 'snacks', label: 'Snacks' },
  { value: 'household', label: 'Household' },
  { value: 'personal-care', label: 'Personal Care' },
  { value: 'frozen-foods', label: 'Frozen Foods' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'other', label: 'Other' },
];

/**
 * Measurement unit options for inventory
 */
export const MEASUREMENT_UNIT_OPTIONS: SelectOption[] = [
  { value: 'kg', label: 'Kilogram (kg)' },
  { value: 'g', label: 'Gram (g)' },
  { value: 'l', label: 'Liter (l)' },
  { value: 'ml', label: 'Milliliter (ml)' },
  { value: 'pcs', label: 'Pieces (pcs)' },
  { value: 'pack', label: 'Pack' },
  { value: 'box', label: 'Box' },
  { value: 'bottle', label: 'Bottle' },
  { value: 'can', label: 'Can' },
  { value: 'bag', label: 'Bag' },
  { value: 'carton', label: 'Carton' },
  { value: 'dozen', label: 'Dozen' },
];

/**
 * Product type options (simple vs variant)
 */
export const PRODUCT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'false', label: 'Simple Product' },
  { value: 'true', label: 'Product with Variants' },
];
